/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState } from 'react';
import { observer } from 'mobx-react';

import { currentDiagramStore, diagramExtensionStore } from 'stores';
import { Dropdown } from 'components';
import { IconButton } from 'primitives';
import { Dots, Editor, Png, Share, Svg, Xml } from 'icons';
import { dedicatedModesStore } from 'App/Pages/Diagram/stores';
import { trackingService } from 'services';
import hasAccess, { actions } from 'utils/user-access';
import { exportDiagramPNG, exportDiagramSVG, exportDiagramXML } from 'utils/file-io';
import { XMLEditorStore } from 'App/Pages/Diagram/XMLEditor';

import * as Styled from './ActionsMenu.styled';

const ActionsMenu = () => {
  const iconSize = 18;

  const { isBPMN, isDMN, isValid: isCurrentDiagramValid } = currentDiagramStore;
  const { diagram, project } = currentDiagramStore.state;
  const { isImplementMode } = dedicatedModesStore;
  const { modeler } = currentDiagramStore;
  const { isEditorOpen } = XMLEditorStore;

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);

  const shouldShowShareItem =
    isCurrentDiagramValid && isBPMN && isImplementMode && hasAccess(project, actions.SHARE_DIAGRAM);

  const isXMLEditorEnabled =
    ((isBPMN && isImplementMode) || isDMN) && hasAccess(project, actions.MODIFY_DIAGRAM) && !isEditorOpen;

  const handleDownloadXML = () => {
    exportDiagramXML(modeler, diagram);
  };

  const handleEditXML = () => {
    XMLEditorStore.openEditor();
  };

  const trackMenuItemClick = (item) => {
    trackingService.trackActionsMenuItemClick(
      item,
      diagram.id,
      diagram.type,
      isBPMN ? dedicatedModesStore.selectedModeLabel : undefined
    );
  };

  return (
    <>
      <IconButton
        // @ts-expect-error TS2769
        $isActive={Boolean(actionsMenuAnchorEl)}
        aria-haspopup="true"
        title="Browse more actions"
        onClick={(event) => {
          setActionsMenuAnchorEl(event.currentTarget);
          trackingService.trackActionsMenuOpening(
            diagram.id,
            diagram.type,
            isBPMN ? dedicatedModesStore.selectedModeLabel : undefined
          );
        }}
        data-test="actions-context-dropdown-button"
      >
        <Dots />
      </IconButton>

      <Dropdown
        anchorEl={actionsMenuAnchorEl}
        open={Boolean(actionsMenuAnchorEl)}
        onClose={() => setActionsMenuAnchorEl(null)}
        data-test="actions-context-dropdown"
      >
        {shouldShowShareItem && (
          // @ts-expect-error TS2741
          <Dropdown.ListGroup>
            {/* @ts-expect-error TS2769 */}
            <Styled.ActionItem
              data-test="share"
              title="Share"
              onClick={() => {
                setActionsMenuAnchorEl(null);
                diagramExtensionStore.showShareModal();
                trackMenuItemClick('share');
              }}
            >
              <Share width={iconSize} height={iconSize} />
              Share
            </Styled.ActionItem>
          </Dropdown.ListGroup>
        )}

        {/* @ts-expect-error TS2741 */}
        <Dropdown.ListGroup>
          {isCurrentDiagramValid && isBPMN && (
            <>
              {/* @ts-expect-error TS2769 */}
              <Styled.ActionItem
                data-test="export-png"
                onClick={() => {
                  setActionsMenuAnchorEl(null);
                  exportDiagramPNG(modeler, diagram);
                  trackMenuItemClick('export-png');
                }}
              >
                <Png width={iconSize} height={iconSize} />
                Export as PNG image
              </Styled.ActionItem>
              {/* @ts-expect-error TS2769 */}
              <Styled.ActionItem
                data-test="export-svg"
                onClick={() => {
                  setActionsMenuAnchorEl(null);
                  exportDiagramSVG(modeler, diagram);
                  trackMenuItemClick('export-svg');
                }}
              >
                <Svg width={iconSize} height={iconSize} />
                Export as SVG image
              </Styled.ActionItem>
            </>
          )}

          {/* @ts-expect-error TS2769 */}
          <Styled.ActionItem
            data-test="export-xml"
            onClick={() => {
              setActionsMenuAnchorEl(null);
              handleDownloadXML();
              trackMenuItemClick('export-xml');
            }}
          >
            <Xml width={iconSize} height={iconSize} />
            Download as {isBPMN ? 'BPMN 2.0' : 'DMN 1.3'} XML
          </Styled.ActionItem>
        </Dropdown.ListGroup>

        {isXMLEditorEnabled && (
          // @ts-expect-error TS2741
          <Dropdown.ListGroup>
            {/* @ts-expect-error TS2769 */}
            <Styled.ActionItem
              data-test="edit-xml"
              onClick={() => {
                setActionsMenuAnchorEl(null);
                handleEditXML();
                trackMenuItemClick('edit-xml');
              }}
            >
              <Editor width={iconSize} height={iconSize} />
              Edit XML
            </Styled.ActionItem>
          </Dropdown.ListGroup>
        )}
      </Dropdown>
    </>
  );
};

export default observer(ActionsMenu);
